import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Store from "../store";
import { LanguageTypes, SSOExternalUserGridListDto } from "../types";
import { useForm } from "react-hook-form";
import cx from "classnames";
import { Button } from "../../../../components";
import { MessageResultState } from "../../../../types/general-types";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import RadioGroupInput, {
  RadioOption,
} from "../../../../components/Input/RadioGroupInput";

interface IViewDetails {
  ExternalUserManagementStore?: Store;
  selectedRow: SSOExternalUserGridListDto | undefined;
  isShow: boolean;
  toggleModal: any;
  t: any;
}

const CreateUpdateModal: FC<IViewDetails> = inject(
  "ExternalUserManagementStore"
)(
  observer((props: IViewDetails) => {
    const {
      ExternalUserManagementStore: store,
      isShow,
      toggleModal,
      selectedRow,
    } = props;
    const { t } = useTranslation();
    const { register, handleSubmit, errors } = useForm();
    const [data, setData] = useState<SSOExternalUserGridListDto>({
      id: 0,
      email: "",
      nameSurname: "",
      password: "",
      username: "",
      phoneNumber: "",
      sendEmailVerification: false,
      sendSmsVerification: false,
      contentLanguage: LanguageTypes.Turkish,
    });

    useEffect(() => {
      if (selectedRow) {
        setData(selectedRow);
      }
    }, [selectedRow]);

    const page = useMemo(() => {
      const languageOptions: RadioOption[] = [
        { id: "Turkish", label: t("Turkish"), value: "1" },
        { id: "English", label: t("English"), value: "2" },
      ];

      return (
        <Modal isOpen={isShow} toggle={toggleModal} className="modal-xl">
          <ModalHeader toggle={toggleModal}>
            {t("External User") +
              " " +
              (selectedRow ? t("Update") : t("Create"))}
          </ModalHeader>
          <ModalBody>
            <>
              <div className="row mb-2">
                <div className="col-md-6">
                  <div className="">
                    <label style={{}} htmlFor="nameSurname">
                      {t("Name Surname")}{" "}
                      <strong className="text-danger">*</strong>
                    </label>
                  </div>
                  <input
                    type="text"
                    value={data?.nameSurname}
                    id={"nameSurname"}
                    name={"nameSurname"}
                    placeholder={t("Name Surname")}
                    ref={register({
                      required: t("Please fill out this field.").toString(),
                      minLength: {
                        value: 3,
                        message: t(
                          "The given value must be {{value}} characters..",
                          { value: 3 }
                        ),
                      },
                    })}
                    className={cx("form-control ", {
                      "is-invalid": errors["nameSurname"],
                    })}
                    onChange={(event: any) => {
                      setData({
                        ...data,
                        nameSurname: event.target.value,
                      });
                    }}
                  />
                  {errors && errors["nameSurname"] && (
                    <div className="invalid-feedback d-block">
                      {errors["nameSurname"].message}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="">
                    <label style={{}} htmlFor="email">
                      {t("Email")} <strong className="text-danger">*</strong>
                    </label>
                  </div>
                  <input
                    type="text"
                    value={data?.email}
                    id={"email"}
                    name={"email"}
                    placeholder={t("Email")}
                    ref={register({
                      required: t("Please fill out this field.").toString(),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t("Invalid Email Address"),
                      },
                    })}
                    className={cx("form-control ", {
                      "is-invalid": errors["email"],
                    })}
                    onChange={(event: any) => {
                      setData({
                        ...data,
                        email: event.target.value,
                      });
                    }}
                  />
                  {errors && errors["email"] && (
                    <div className="invalid-feedback d-block">
                      {errors["email"].message}
                    </div>
                  )}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <div className="">
                    <label style={{}} htmlFor="username">
                      {t("Username")} <strong className="text-danger">*</strong>
                    </label>
                  </div>
                  <input
                    type="text"
                    value={data?.username}
                    id={"username"}
                    name={"username"}
                    placeholder={t("Username")}
                    ref={register({
                      required: t("Please fill out this field.").toString(),
                      minLength: {
                        value: 5,
                        message: t(
                          "The given value must be {{value}} characters..",
                          { value: 5 }
                        ),
                      },
                    })}
                    className={cx("form-control ", {
                      "is-invalid": errors["username"],
                    })}
                    onChange={(event: any) => {
                      setData({
                        ...data,
                        username: event.target.value,
                      });
                    }}
                  />
                  {errors && errors["username"] && (
                    <div className="invalid-feedback d-block">
                      {errors["username"].message}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="">
                    <label style={{}} htmlFor="password">
                      {t("Password")} <strong className="text-danger">*</strong>
                    </label>
                  </div>
                  <input
                    type="text"
                    value={data?.password}
                    id={"password"}
                    name={"password"}
                    placeholder={t("Password")}
                    ref={register({
                      required: t("Please fill out this field.").toString(),
                      minLength: {
                        value: 5,
                        message: t(
                          "The given value must be {{value}} characters..",
                          { value: 5 }
                        ),
                      },
                    })}
                    className={cx("form-control ", {
                      "is-invalid": errors["password"],
                    })}
                    onChange={(event: any) => {
                      setData({
                        ...data,
                        password: event.target.value,
                      });
                    }}
                  />
                  {errors && errors["password"] && (
                    <div className="invalid-feedback d-block">
                      {errors["password"].message}
                    </div>
                  )}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <div className="">
                    <label style={{}} htmlFor="phoneNumber">
                      {t("Phone Number")}{" "}
                      <strong className="text-danger">*</strong>
                    </label>
                  </div>
                  <NumberFormat
                    type="text"
                    getInputRef={register({
                      required: t("Please fill out this field.") as string,
                      minLength: {
                        value: 10,
                        message: t("Please fill out this field.", {
                          value: 10,
                        }),
                      },
                      maxLength: {
                        value: 18,
                        message: t("Please fill out this field.", {
                          value: 18,
                        }),
                      },
                    })}
                    required
                    name="phoneNumber"
                    format="(###) ###-####"
                    mask="_"
                    className={cx("form-control ", {
                      "is-invalid": errors["phoneNumber"],
                    })}
                    value={data.phoneNumber}
                    placeholder={t("Phone Number")}
                    onValueChange={(value, event) => {
                      setData({ ...data, phoneNumber: value.value });
                    }}
                  />

                  {errors["phoneNumber"] && (
                    <div className="invalid-feedback d-block">
                      {errors["phoneNumber"].message}
                    </div>
                  )}
                </div>
                <div className="col-md-3">
                  <label htmlFor="">
                    {t("Verification Method")}{" "}
                    <strong className="text-danger">*</strong>
                  </label>
                  {errors && (errors["sms"] || errors["mail"]) && (
                    <div className="invalid-feedback d-block">
                      {errors["sms"].message ?? errors["mail"].message}
                    </div>
                  )}
                  <div className="d-flex align-items-center ">
                    <input
                      id={"sms"}
                      name={"sms"}
                      type="checkbox"
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "5px",
                      }}
                      checked={data.sendSmsVerification}
                      onChange={(event: any) => {
                        const { checked } = event.target;
                        setData({ ...data, sendSmsVerification: checked });
                      }}
                      ref={register({
                        required:
                          !data.sendEmailVerification &&
                          t("Please fill out this field.").toString(),
                      })}
                    />
                    <label htmlFor="sms">{t("SMS")}</label>
                  </div>
                  <div className="d-flex align-items-center">
                    <input
                      id={"mail"}
                      name={"mail"}
                      type="checkbox"
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "5px",
                      }}
                      checked={data.sendEmailVerification}
                      onChange={(event: any) => {
                        const { checked } = event.target;
                        setData({ ...data, sendEmailVerification: checked });
                      }}
                      ref={register({
                        required:
                          !data.sendSmsVerification &&
                          t("Please fill out this field.").toString(),
                      })}
                    />
                    <label htmlFor="mail">{t("Email")}</label>
                  </div>
                </div>
                <div className="col-md-3">
                  <label htmlFor="">
                    {t("Content Language")}{" "}
                    <strong className="text-danger">*</strong>
                  </label>
                  <RadioGroupInput
                    name="language"
                    layout="stacked"
                    options={languageOptions}
                    selectedValue={String(data.contentLanguage)}
                    onChange={(value: string) => {
                      const contentLanguage =
                        value === "1"
                          ? LanguageTypes.Turkish
                          : LanguageTypes.English;
                      setData({ ...data, contentLanguage: contentLanguage });
                    }}
                    register={register}
                    isRequired={true}
                    errors={errors}
                    t={t}
                  />
                </div>
              </div>
            </>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-light mr-auto" onClick={toggleModal}>
              <i className="fas fa-times mr-2"></i>
              {t("Close")}
            </button>
            <Button
              className="btn "
              style={{
                backgroundColor: "#23486A",
                color: "#faf5f5",
              }}
              onClick={handleSubmit(async () => {
                if (!data.sendSmsVerification && !data.sendEmailVerification) {
                  toast.warning(
                    t("Please select at least one verification method")
                  );
                  return;
                }
                if (data && data.id === 0) {
                  await store
                    ?.create({
                      email: data.email.trim(),
                      nameSurname: data.nameSurname.trim(),
                      password: data.password.trim(),
                      username: data.username.trim(),
                      phoneNumber: data.phoneNumber.trim(),
                      sendSmsVerification: data.sendSmsVerification,
                      sendEmailVerification: data.sendEmailVerification,
                      contentLanguage: data.contentLanguage,
                    })
                    .then((res) => {
                      if (res.state === MessageResultState.SUCCESS) {
                        toggleModal();
                      }
                    });
                } else {
                  await store
                    ?.update({
                      id: data.id,
                      email: data.email.trim(),
                      nameSurname: data.nameSurname.trim(),
                      password: data.password.trim(),
                      username: data.username.trim(),
                      phoneNumber: data.phoneNumber.trim(),
                      sendSmsVerification: data.sendSmsVerification,
                      sendEmailVerification: data.sendEmailVerification,
                      contentLanguage: data.contentLanguage,
                    })
                    .then((res) => {
                      if (res.state === MessageResultState.SUCCESS) {
                        toggleModal();
                      }
                    });
                }
              })}
              icon={<i className="fas fa-save mr-2"></i>}
              text={t("Save")}
              showConfirm={true}
            />
          </ModalFooter>
        </Modal>
      );
    }, [
      isShow,
      toggleModal,
      t,
      selectedRow,
      data,
      register,
      errors,
      handleSubmit,
      store,
    ]);

    return <>{page}</>;
  })
);

export default CreateUpdateModal;
